import { Navigate, useNavigate } from 'react-router';

import {
  AgreementLegalContextSettings,
  AgreementConditionsSettings,
  AgreementJurisdictionLiabilityCard,
} from 'src/containers/Escrow/Agreement';
import { useSoftwareEscrow } from 'src/context/software-escrow';
import {
  AgreementContextProvider,
  useAgreementSuggestChanges,
} from 'src/context/suggest-changes/AgreementSuggestChangesContext';
import { JurisdictionEnum, LiabilityEnum, type IAgreement, type IAgreementUpdateParams } from 'src/graphql/schema';
import {
  SoftwareOnboardingLayout,
  SoftwareOnboardingHeader,
  SoftwareOnboardingMain,
  SoftwareOnboardingFooter,
} from 'src/components/SoftwareOnboarding';
import { ExitAlertDialog } from 'src/pages/SoftwareEscrowRequestChanges/ui';
import { getRequestChangesExitPath } from 'src/pages/SoftwareEscrowRequestChanges/lib';
import { SoftwareEscrowRequestChangesRoutesEnum } from 'src/pages/SoftwareEscrowRequestChanges/model';

import { SubmitChangesDialog } from './ui';

const Content = () => {
  const {
    softwareEscrow: { id, agreement },
  } = useSoftwareEscrow();
  const navigate = useNavigate();
  const {
    state,
    updateConditions,
    updateContext,
    updateJurisdiction,
    updateLiability,
    updateOtherContext,
    updateOtherCondition,
  } = useAgreementSuggestChanges();

  const onDiscardChanges = () =>
    navigate(getRequestChangesExitPath(id, SoftwareEscrowRequestChangesRoutesEnum.Agreement));

  const onJurisdictionUpdate = ({ jurisdiction }: IAgreementUpdateParams) =>
    updateJurisdiction({
      value: jurisdiction as JurisdictionEnum,
      hasChanges: hasChanges(agreement?.jurisdiction ?? '', jurisdiction ?? ''),
    });

  const onLiabilityUpdate = ({ liability }: IAgreementUpdateParams) =>
    updateLiability({
      value: liability as LiabilityEnum,
      hasChanges: hasChanges(agreement?.liability ?? '', liability ?? ''),
    });

  return (
    <SoftwareOnboardingLayout>
      <SoftwareOnboardingHeader justify="end">
        <ExitAlertDialog onDiscard={onDiscardChanges} />
      </SoftwareOnboardingHeader>
      <SoftwareOnboardingMain
        subtitle="Request changes to agreement information:"
        title="Details"
        noteText="These selections will tailor the escrow agreement to your specific arrangement."
        classes={{
          note: 'not-italic [&>span]:hidden',
        }}
      >
        <div className="max-w-260 w-full mx-auto space-y-4">
          <AgreementLegalContextSettings
            onUpdate={({ legalContext }) =>
              updateContext({
                values: legalContext || [],
                hasChanges: hasChanges(agreement?.legalContext ?? [], legalContext || []),
              })
            }
            onOtherChange={(legalContextOther) =>
              updateOtherContext({
                value: legalContextOther,
                hasChanges: hasChanges(agreement?.legalContextOther ?? '', legalContextOther),
              })
            }
            legalContext={state.data.context}
            legalContextOther={state.data.otherContext}
          />
          <AgreementConditionsSettings
            title="Release events"
            description={`Select from the standard terms, or define the specific conditions under which the beneficiary can request the release of the escrow materials.\n\nChoose one, or all that are applicable.`}
            onUpdate={({ conditions }) =>
              updateConditions({
                values: conditions || [],
                hasChanges: hasChanges(agreement?.conditions ?? [], conditions || []),
              })
            }
            onOtherChange={(conditionsOther) =>
              updateOtherCondition({
                value: conditionsOther,
                hasChanges: hasChanges(agreement?.conditionsOther ?? '', conditionsOther),
              })
            }
            conditions={state.data.conditions}
            conditionsOther={state.data.conditionsOther}
          />
          <AgreementJurisdictionLiabilityCard
            jurisdiction={state.data.jurisdiction}
            liability={state.data.liability}
            onJurisdictionUpdate={onJurisdictionUpdate}
            onLiabilityUpdate={onLiabilityUpdate}
          />
        </div>
      </SoftwareOnboardingMain>
      <SoftwareOnboardingFooter justify="end">
        <SubmitChangesDialog />
      </SoftwareOnboardingFooter>
    </SoftwareOnboardingLayout>
  );
};

function hasChanges<T extends string | string[]>(oldValue: T, newValue: T) {
  if (Array.isArray(newValue) && Array.isArray(oldValue)) {
    const sortedNew = [...newValue].sort();
    const sortedOld = [...oldValue].sort();

    if (sortedNew.length !== sortedOld.length) return true;
    for (let i = 0; i < sortedNew.length; i++) {
      if (sortedNew[i] !== sortedOld[i]) return true;
    }
    return false;
  }
  return newValue !== oldValue;
}

const SoftwareEscrowRequestChangesAgreement = () => {
  const {
    softwareEscrow: { id: escrowId, agreement, policy },
  } = useSoftwareEscrow();

  if (!policy.createAgreementSuggestedChange)
    return (
      <Navigate to={getRequestChangesExitPath(escrowId, SoftwareEscrowRequestChangesRoutesEnum.Agreement)} replace />
    );

  return (
    <AgreementContextProvider escrowId={escrowId} agreement={agreement as IAgreement}>
      <Content />
    </AgreementContextProvider>
  );
};

export default SoftwareEscrowRequestChangesAgreement;
